import React, { useState, useEffect } from "react";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import "./settings.css";
import Box from "@mui/material/Box";
import CircularProgress, {
  circularProgressClasses,
} from "@mui/material/CircularProgress";
import {
  fetchAllPermissions,
  fetchAllRoles,
  fetchShipperUsers,
} from "../../../../../services/settings.services";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import SideBar from "./Components/SideBar/SideBar";
import ManagePermissions from "./Components/ManagePermissions/ManagePermissions";
import Sad from "@mui/icons-material/SentimentVeryDissatisfied";

const WAREHOUSE_PERMISSION_ROOTS = [
  "shipper.access.product",
  "shipper.access.order",
  "shipper.access.payment",
];

// Temporary: only expose these specific leaf permissions to warehouse users.
const WAREHOUSE_ALLOWED_CHILDREN = [
  "shipper.access.product.view",
  "shipper.access.order.view",
  "shipper.access.payment.view",
];

const SHIPPER_PERMISSION_ROOTS = [
  "shipper.access.shipment",
  // "shipper.access.invoice",  // no /Invoices route registered
  // "shipper.access.pickup",   // no /Pickup route registered
  // "shipper.access.tracking", // no /Tracking route registered
  "shipper.access.product",
  // "shipper.access.carrier", // hidden for now
  "shipper.access.order",
  "shipper.access.payment",
];

// Phase 1: expose only the `.view` leaf permissions. Granting view = full module access.
const SHIPPER_ALLOWED_CHILDREN = [
  "shipper.access.shipment.view",
  // "shipper.access.invoice.view",  // no /Invoices route registered
  // "shipper.access.pickup.view",   // no /Pickup route registered
  // "shipper.access.tracking.view", // no /Tracking route registered
  "shipper.access.product.view",
  // "shipper.access.carrier.view", // hidden for now
  "shipper.access.order.view",
  "shipper.access.payment.view",
];

// Keep hidden children in the data (marked `hidden: true`) so toggling a
// visible permission can also toggle its hidden siblings.
const filterPermissionTree = (allPermissions, roots, allowedChildren) =>
  allPermissions
    .filter((p) => roots.includes(p.name))
    .map((p) => ({
      ...p,
      children: (p.children ?? []).map((c) => ({
        ...c,
        hidden: !allowedChildren.includes(c.name),
      })),
    }));

function PermissionsSettings(props) {
  const { t } = useTranslation("settings");
  const user = useSelector((state) => state.user);
  const [loading, setLoading] = useState(false);

  //create roles

  const [permisions, setPermissions] = useState([]);
  const [permisionsAlter, setPermissionsAlter] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const setSnack = useSnackBar();

  const fetchPermissionsData = async () => {
    const saved = await fetchAllPermissions();
    if (saved?.error) throw saved.error;
    const allPermissions = saved?.data?.result?.permissions?.data ?? [];
    let permissionsList;
    if (user?.role === "warehouseUser") {
      permissionsList = filterPermissionTree(
        allPermissions,
        WAREHOUSE_PERMISSION_ROOTS,
        WAREHOUSE_ALLOWED_CHILDREN
      );
    } else if (
      user?.role === "Shipper" ||
      user?.role === "ShipperUser" ||
      user?.role === "oneTimeShipper"
    ) {
      permissionsList = filterPermissionTree(
        allPermissions,
        SHIPPER_PERMISSION_ROOTS,
        SHIPPER_ALLOWED_CHILDREN
      );
    } else {
      permissionsList = allPermissions;
    }
    setPermissions(permissionsList);
    const array = permissionsList.map((obj) => ({
      key1: false,
      key2: (obj?.children ?? []).map(() => ({ subKey: false })),
    }));
    setPermissionsAlter(array);
  };
  const fetchAllRolesData = async () => {
    const saved = await fetchAllRoles();
    if (saved?.error) throw saved.error;
    const rolesList = saved?.data?.result?.data ?? [];
    setRoles(
      rolesList.map((role) => ({
        id: role.id,
        name: role.name,
        permissions: (role.permissions ?? []).map((p) => p.id),
      }))
    );
  };
  const fetchShipperUsersData = async () => {
    const saved = await fetchShipperUsers();
    if (saved?.error) throw saved.error;
    const usersList = saved?.data?.result?.data ?? [];
    setUsers(
      usersList.map((u) => ({
        id: u.id,
        name: u.username,
        roleName: u?.roles?.length > 0 ? u?.roles[0]?.name : null,
        roles: (u.roles ?? []).map((r) => r.id),
        permissions: [
          ...(u.permissions ?? []).map((p) => p.id),
          ...(u.roles ?? []).flatMap((role) =>
            (role.permissions ?? []).map((permission) => permission.id)
          ),
        ],
        rolesPermissions: (u.roles ?? []).flatMap((role) =>
          (role.permissions ?? []).map((permission) => permission.id)
        ),
      }))
    );
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchPermissionsData(),
        fetchAllRolesData(),
        fetchShipperUsersData(),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
      setPermissions([]);
      setPermissionsAlter([]);
      setRoles([]);
      setUsers([]);
      setSnack({
        open: true,
        vertical: "top",
        horizontal: "center",
        text:
          error?.message ||
          t("permissions.loadFailed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      <div className="parent_wraper">
        {/* sidebar code  */}
        <SideBar active="0_1" />
        <div className="dash_table_wrapper">
          {/* right side secions  */}
          <div className="flex items-center justify-between px-1 mb-4">
            <h2 className="text-lg font-semibold text-ui-text m-0">{t("permissions.title")}</h2>
          </div>
          {(loading || (roles.length >= 1 && permisions.length >= 1 && (user.role !== "Shipper" || users.length >= 1))) && (
            <>
              <ManagePermissions
                loading={loading}
                isShipper={user.role === "Shipper" ? true : false}
                users={
                  user.role === "Shipper"
                    ? users
                    : [
                        {
                          ...user,
                          id: user.id,
                          name: user.username,
                          roleName:
                            user?.roles?.length > 0
                              ? user?.roles[0]?.name
                              : null,
                          roles: user.roles.map((r) => r.id),
                          permissions: [
                            ...user.permissions.map((p) => p.id),
                            ...user.roles.flatMap((role) =>
                              role.permissions.map(
                                (permission) => permission.id
                              )
                            ),
                          ],
                          rolesPermissions: user.roles.flatMap((role) =>
                            role.permissions.map((permission) => permission.id)
                          ),
                        },
                      ]
                }
                roles={roles}
                permisions={permisions}
                setUsers={setUsers}
                setSnack={setSnack}
              />
            </>
          )}
          {roles.length < 1 && permisions.length < 1 && !loading && (
            <div className="driver__screen_empty" style={{ width: "auto" }}>
              <div className="driver__scrnempt_content">
                <Sad className="driver__scrnempt_contenticon" />
                <div>{t("permissions.empty")}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PermissionsSettings;

function Loader(props) {
  return (
    <Box
      sx={{
        position: "relative",
        top: "2px",
      }}
    >
      <CircularProgress
        variant="determinate"
        sx={{
          color: (theme) =>
            theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
        }}
        size={20}
        thickness={4}
        {...props}
        value={100}
      />
      <CircularProgress
        variant="indeterminate"
        disableShrink
        sx={{
          color: (theme) =>
            theme.palette.mode === "light" ? "var(--color-brand)" : "#308fe8",
          animationDuration: "550ms",
          position: "absolute",
          left: 0,
          [`& .${circularProgressClasses.circle}`]: {
            strokeLinecap: "round",
          },
        }}
        size={20}
        thickness={4}
        {...props}
      />
    </Box>
  );
}
